import Component from '@glimmer/component';
import { service } from '@ember/service';
import AddSomethingModal, {
  type OpenArgs,
} from 'donor-elf-web/services/add-something-modal';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import Store from '@ember-data/store';
import CampaignService from 'donor-elf-web/services/campaign-service';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import PledgeValidation from 'donor-elf-web/validations/pledge';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import {
  calculateEndDateForOneTimePledge,
  calculateEndDateMonthsForOneTimePledge,
  pledgeFrequencyLabels,
  pledgeOneTimeFrequency,
} from 'donor-elf-web/lib/pledge-utils';
import PledgeModel from 'donor-elf-web/models/pledge';
import AbilitiesService from 'donor-elf-web/services/abilities';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import JournalContactDetailModel from 'donor-elf-web/models/all-funds/journal-contact-detail';
import { isEmpty, isPresent } from '@ember/utils';
import CampaignPledgeModel from 'donor-elf-web/models/campaign-pledge';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';

interface ComponentArgs {
  openArgs?: OpenArgs;
}

export default class AddSomethingModalPledgeIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.createChangeset();
    this.loadJournalContactDetails();
    this.loadCampaignPledge();
  }

  @service declare abilities: AbilitiesService;
  @service declare addSomethingModal: AddSomethingModal;
  @service declare campaignService: CampaignService;
  @service declare fundGoalService: FundGoalService;
  @service declare store: Store;

  @tracked record!: PledgeModel;
  @tracked changeset: any;
  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked saveError?: any;

  @tracked loadingCampaignPledge = false;
  @tracked campaignPledgeChangeset: any;

  @tracked journalContactDetails: JournalContactDetailModel[] = [];

  createChangeset() {
    this.record =
      this.args.openArgs?.pledgeOptions?.existingPledge ||
      this.store.createRecord('pledge', {
        contactId: this.args.openArgs?.pledgeOptions?.contactId,
        amount: this.args.openArgs?.pledgeOptions?.amount,
        frequency: this.args.openArgs?.pledgeOptions?.frequency,
        startDate: this.args.openArgs?.pledgeOptions?.startDate,
      });

    this.changeset = Changeset(
      this.record,
      lookupValidator(PledgeValidation),
      PledgeValidation,
    );

    let endDateMonths = '12';

    if (this.record.frequency === pledgeOneTimeFrequency) {
      let calculatedMonths = calculateEndDateMonthsForOneTimePledge({
        startDate: this.record.startDate,
        endDate: this.record.endDate,
      });

      if (calculatedMonths) {
        endDateMonths = calculatedMonths.toString();
      }
    }

    setTimeout(() => {
      this.changeset.set('endDateMonths', endDateMonths);
    }, 0);
  }

  async loadJournalContactDetails() {
    if (this.args.openArgs?.pledgeOptions?.existingPledge) {
      this.journalContactDetails = (
        await this.store.query('all-funds/journal-contact-detail', {
          filter: {
            contact_id:
              this.args.openArgs.pledgeOptions.existingPledge.contactId,
          },
        })
      ).slice();
    }
  }

  async loadCampaignPledge() {
    if (this.showCampaign) {
      this.loadingCampaignPledge = true;
      let campaignPledges = (
        await this.store.query('campaign-pledge', {
          filter: { pledge_ids: [this.record.id] },
        })
      ).slice();
      if (campaignPledges.length > 0) {
        this.campaignPledgeChangeset = Changeset(campaignPledges[0]!);
      } else {
        this.campaignPledgeChangeset = Changeset(
          this.store.createRecord('campaign-pledge', {
            pledgeId: this.record.id,
          }),
        );
      }
      this.loadingCampaignPledge = false;
    }
  }

  get isNewRecord(): boolean {
    return this.record.isNew as unknown as boolean;
  }

  get cannotEditAllFields() {
    return (
      !this.isNewRecord &&
      !this.record.isManual &&
      this.abilities.noAbility('edit_pledges')
    );
  }

  get pledgeIsPartOfJournalDecision() {
    if (this.isNewRecord) {
      return false;
    }
    let pledge = this.record;
    return this.journalContactDetails.some((detail) =>
      detail.journalDecisions.slice().some((x) => x.pledgeId === pledge.id),
    );
  }

  get cantDeleteReason(): SafeString | undefined {
    let message = undefined;
    if (!this.record.isManual && !this.abilities.hasAbility('edit_pledges')) {
      message =
        "<p>Sorry, you can't delete this pledge because it comes from your organization's accounting system.</p><div>Set the End Date on the pledge if the donor is no longer giving.</div>";
    }
    if (this.pledgeIsPartOfJournalDecision) {
      message =
        "Sorry, you can't delete this pledge because it's part of a Journal decision. You can set the End Date on the pledge if the donor is no longer giving.";
    }
    if (message) {
      return htmlSafe(message);
    } else {
      return undefined;
    }
  }

  get isOneTimeFrequency() {
    return this.changeset.get('frequency') === pledgeOneTimeFrequency;
  }

  get oneTimeMonthlyAmount() {
    return (
      (this.changeset.get('amount') || 0) /
      Number(this.changeset.get('endDateMonths'))
    );
  }

  get hasYtdGoalType() {
    return this.fundGoalService.hasYtdGoalType;
  }

  get frequencyOptions(): SelectOption[] {
    let options: SelectOption[] = [
      {
        val: undefined,
        label: '',
      },
    ];
    for (let key in pledgeFrequencyLabels) {
      if (pledgeFrequencyLabels[key] !== 'Weekly') {
        options.push({
          val: Number(key),
          label: pledgeFrequencyLabels[key]!,
        });
      }
    }
    return options;
  }

  get showCampaign() {
    return (
      this.campaignService.campaignsEnabled &&
      this.campaignService.campaigns.length > 0
    );
  }

  get campaignOptions(): SelectOption[] {
    return [
      { val: undefined, label: '' },
      ...this.campaignService.sortedCampaignSelectOptions,
    ];
  }

  @action
  handleFrequencyChanged(/* newFrequency */) {
    this.changeset.set('endDateMonths', '12');
  }

  @action
  writeFindPledgeInRubyToClipboard() {
    window.navigator.clipboard.writeText(
      `pledge = Pledge.find(${this.record.id})`,
    );
  }

  @action
  async save() {
    if (this.saving) {
      return;
    }
    this.showValidationErrors = false;
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      try {
        this.saving = true;
        let { isNewRecord } = this;
        if (this.changeset.get('frequency') === pledgeOneTimeFrequency) {
          this.changeset.set(
            'endDate',
            calculateEndDateForOneTimePledge({
              startDate: this.changeset.get('startDate'),
              endDateMonths: this.changeset.get('endDateMonths'),
              hasYtdGoalType: this.hasYtdGoalType,
            }),
          );
        }
        await this.changeset.save();
        let pledge = this.changeset.get('data');

        let campaignPledge: CampaignPledgeModel | undefined = undefined;
        if (this.campaignPledgeChangeset) {
          campaignPledge = this.campaignPledgeChangeset.get('data');
          if (isNewRecord) {
            if (isPresent(this.campaignPledgeChangeset.get('campaignId'))) {
              this.campaignPledgeChangeset.set('pledgeId', pledge.id);
              await this.campaignPledgeChangeset.save();
            }
          } else {
            if (isEmpty(this.campaignPledgeChangeset.get('campaignId'))) {
              await campaignPledge!.destroyRecord();
              campaignPledge = undefined;
            } else if (this.campaignPledgeChangeset.isDirty) {
              await this.campaignPledgeChangeset.save();
            }
          }
        }
        if (campaignPledge) {
          this.campaignService.reloadCampaignContactsIfContactIdNotInCampaign({
            contactId: pledge.contactId,
            campaignId: campaignPledge.campaignId,
          });
        }
        if (this.showCampaign) {
          this.campaignService.reloadActiveCampaignGoals();
        }

        this.addSomethingModal.pledgeSaved({
          pledge,
          campaignPledge,
          newPledge: isNewRecord,
          deleted: false,
        });
        this.addSomethingModal.close();
      } catch (e) {
        this.saveError = e;
      }
      this.saving = false;
    } else {
      this.showValidationErrors = true;
    }
  }

  @action
  async delete() {
    if (this.saving || this.isNewRecord) {
      return;
    }
    try {
      let pledge = this.record;
      await pledge.destroyRecord();
      if (this.showCampaign) {
        this.campaignService.reloadActiveCampaignGoals();
      }
      this.addSomethingModal.pledgeSaved({
        pledge,
        newPledge: false,
        deleted: true,
      });
      this.close();
    } catch (e) {
      this.saveError = e;
    }
    this.saving = false;
  }

  @action
  close() {
    this.addSomethingModal.close();
  }
}
